import type { ReactNode } from 'react'
import {
  BarChart3,
  CloudDownload,
  CloudSun,
  Compass,
  Crosshair,
  LandPlot,
  Map as MapIcon,
  Medal,
  Package,
  Radar,
  Trophy,
  Waves,
  Wind,
} from 'lucide-react'

export type FeatureItem = {
  icon: ReactNode
  title: string
  description?: string
}

const FEATURES: FeatureItem[] = [
  {
    icon: <MapIcon className="size-5" />,
    title: 'Full map toolkit',
    description: 'Properties, zones, blinds & unlimited pins',
  },
  {
    icon: <LandPlot className="size-5" />,
    title: 'Landowner parcels',
    description: 'Property lines with ownership info',
  },
  {
    icon: <CloudDownload className="size-5" />,
    title: 'Offline maps',
    description: 'Satellite imagery for your places, off-grid',
  },
  {
    icon: <Radar className="size-5" />,
    title: 'NEXRAD Radar',
    description: 'Live precipitation on your map',
  },
  {
    icon: <Wind className="size-5" />,
    title: 'Wind Map',
    description: 'Animated wind across the map',
  },
  {
    icon: <Compass className="size-5" />,
    title: 'Wind Compass',
    description: 'True wind at your GPS position',
  },
  {
    icon: <Waves className="size-5" />,
    title: 'River Gauges',
    description: 'Live river levels & hydrographs on the map',
  },
  {
    icon: <Crosshair className="size-5" />,
    title: 'SpreadIQ',
    description: "Decoy spreads dialed to today's wind",
  },
  {
    icon: <CloudSun className="size-5" />,
    title: 'Hunt Conditions',
    description: 'Moon phase & river levels on every hunt',
  },
  {
    icon: <Package className="size-5" />,
    title: 'Gear Locker',
    description: 'Loadouts that show which setups produce',
  },
  {
    icon: <BarChart3 className="size-5" />,
    title: 'Advanced analytics',
    description: 'YoY, time-of-day, weather & gear splits',
  },
  {
    icon: <Trophy className="size-5" />,
    title: 'Trophy Room',
    description: 'Stamps, ladders & celebrations',
  },
  {
    icon: <Medal className="size-5" />,
    title: 'Grand Slam tracker',
    description: 'Slam progress across every species',
  },
]

export function ProFeatureList() {
  return (
    <div id="pro-feature-list" data-testid="pro-feature-list">
      <p className="mb-1 text-xs font-bold uppercase tracking-[0.16em] text-primary">
        Included Now
      </p>
      <p className="mb-4 text-sm text-muted-foreground">Everything in Free, plus:</p>
      <div className="flex flex-col gap-4">
        {FEATURES.map((feature) => (
          <FeatureRow key={feature.title} feature={feature} />
        ))}
      </div>
    </div>
  )
}

export function FeatureRow({ feature }: { feature: FeatureItem }) {
  return (
    <div className="flex gap-3" data-testid="pro-feature-row">
      <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary">
        {feature.icon}
      </div>
      <div className="flex flex-1 flex-col justify-center">
        <span className="text-sm font-semibold text-foreground">{feature.title}</span>
        {feature.description ? (
          <p className="mt-0.5 text-sm text-muted-foreground">{feature.description}</p>
        ) : null}
      </div>
    </div>
  )
}
